<template>
	<div :class="['img__wrapper']" :style="wrapperStyle">
		<template v-if="!isError && imageSrc">
			<img
				:class="['img', classname]"
				:src="imageSrc"
				:alt="alt"
				@error="handleError"
			/>
		</template>
		<template v-else-if="isError">
			<img
				:class="['img', classname]"
				:src="fallbackImage"
				:alt="alt"
			/>
		</template>
		<template v-else>
			<Loader
				class="w-full h-full animate-spin text-primary"
				alt="preloader"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";

import { isRadarFileUrl, resolveAuthorizedFileUrl } from "@/core/authorizedFile";
import anonImage from '@/shared/assets/img/anon.png';
import { LoaderCircle as Loader } from 'lucide-vue-next';

const props = withDefaults(
	defineProps<{
		src: string;
		alt: string;
		classname?: string;
		lazy?: boolean;
		fallback?: string;
		cache?: boolean;
		size?: number;
	}>(),
	{
		lazy: false,
		fallback: anonImage,
		cache: true,
		size: 60,
	}
);

const { src, alt, classname, lazy, fallback, size } = toRefs(props);

const imageSrc = ref<string | null>(null);
const isError = ref<boolean>(false);
const isLoading = ref<boolean>(true)

const wrapperStyle = computed(() => ({
	width: `${size.value}px`,
	height: `${size.value}px`,
}));

const fallbackImage = computed(() => fallback.value || anonImage);

const handleError = () => {
	isError.value = true;
};

const loadImage = (source: string) => {
	isLoading.value = true;
	isError.value = false;
	imageSrc.value = null;

	if (!source.trim()) {
		isError.value = true;
		isLoading.value = false;
		return;
	}

	const show = (finalSrc: string) => {
		if (src.value !== source) return;

		if (!lazy.value) {
			imageSrc.value = finalSrc;
			isLoading.value = false;
			return;
		}

		const img = new Image();
		img.src = finalSrc;
		img.onload = () => {
			if (src.value !== source) return;
			imageSrc.value = finalSrc;
			isLoading.value = false;
		};
		img.onerror = () => {
			if (src.value !== source) return;
			isError.value = true;
			isLoading.value = false;
		};
	};

	if (!isRadarFileUrl(source)) {
		show(source);
		return;
	}

	void resolveAuthorizedFileUrl(source)
		.then(show)
		.catch(() => {
			if (src.value !== source) return;
			isError.value = true;
			isLoading.value = false;
		});
};

watch([src, lazy], () => {
	loadImage(src.value);
}, { immediate: true });

</script>

<style scoped>


.img {
	width: 100%;
	height: 100%;
	border-radius: 50%;
}
</style>
